import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import CheckoutDialog from '../components/CheckoutDialog';

const EventDetailBooking = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  const [selectedHorse, setSelectedHorse] = useState('');
  const [selectedClassification, setSelectedClassification] = useState('');
  const [comments, setComments] = useState('');
  const [checkoutVisible, setCheckoutVisible] = useState(false);

  // Mock event data - in real app this would come from API based on eventId
  const event = {
    id: eventId,
    name: "Epworth 2025",
    date: "Saturday 5th October 2025",
    location: "Manor Farm, Epworth, Lincolnshire - DN9 1EP",
    what3words: "///winds.gather.chapel",
    description: "A scenic ride through the historic Isle of Axholme countryside, following ancient drove roads and field paths. Beautiful open views across the farmland with opportunities to spot local wildlife including hares, foxes and various bird species. Mostly level terrain with some gentle undulations.",
    status: "available", // "available" | "full" | "cancelled"
    lastCancellationDate: "Monday 29th September 2025",
    organizer: {
      name: "Sarah Mitchell",
      email: "sarah.mitchell@example.com"
    },
    classes: [
      { id: 'pr-8', name: 'Pleasure Ride 8 miles', memberPrice: 16, nonMemberPrice: 21, earlyBirdDiscount: 2 },
      { id: 'pr-14', name: 'Pleasure Ride 14 miles', memberPrice: 18, nonMemberPrice: 23, earlyBirdDiscount: 3 },
      { id: 'lr', name: 'Lead Rein 2.5 miles', memberPrice: 8, nonMemberPrice: 8, earlyBirdDiscount: 0 }
    ],
    startTimes: "Start any time between 9.00am and 11.30am. 14 milers please start by 11.00am. Gates open at 8.30am.",
    venueType: "Large grass field with good access. Hard standing available for trailers near the farm buildings.",
    facilities: "Toilets available, light refreshments and hot drinks on site.",
    photographer: "www.countryridepics.co.uk",
    onDayEntry: "CASH only £18 member, £23 non-member, £8 junior",
    mapUrl: `https://maps.google.com/maps/api/staticmap?center=DN9%201EP&zoom=14&size=420x300&maptype=roadmap&markers=color:ORANGE|label:Location|DN9%201EP&sensor=false`
  };

  const horses = [
    { label: 'Thunder', value: 'thunder' },
    { label: 'Lightning', value: 'lightning' },
    { label: 'Storm', value: 'storm' },
    { label: 'Unregistered Horse', value: 'unregistered' }
  ];

  const handleBooking = () => {
    if (!selectedHorse || !selectedClassification) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select both a horse and classification before booking.',
        life: 3000
      });
      return;
    }

    setCheckoutVisible(true);
  };

  const handleWaitlist = () => {
    const email = prompt('Enter your email address to join the waitlist:');
    if (email) {
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `You have been added to the waitlist for ${event.name}. You will be notified when spaces become available.`,
        life: 5000
      });
    }
  };

  return (
    <div>
      <Toast ref={toast} />
      {/* Back Button */}
      <div className="flex justify-content-start mb-3">
        <Button 
          icon="pi pi-arrow-left" 
          label="Back to Events" 
          className="p-button-text" 
          onClick={() => navigate('/events')}
        />
      </div>

      {/* Event Header */}
      <div className="se-card mb-4">
        <div className="se-card-content">
          <div className="flex flex-column md:flex-row md:align-items-start gap-4">
            <div className="flex-1">
              <h1 className="mt-0 mb-2 se-text-primary">{event.name}</h1>
              <p className="text-600 mb-3">{event.description}</p>
              
              <div className="grid">
                <div className="col-12 md:col-6">
                  <div className="mb-3">
                    <h3 className="mt-0 mb-2">
                      <i className="pi pi-calendar mr-2"></i>
                      Date & Time
                    </h3>
                    <p className="m-0 font-bold">{event.date}</p>
                    <p className="m-0 text-600">{event.startTimes}</p>
                  </div>
                </div>
                
                <div className="col-12 md:col-6">
                  <div className="mb-3">
                    <h3 className="mt-0 mb-2">
                      <i className="pi pi-map-marker mr-2"></i>
                      Location
                    </h3>
                    <p className="m-0">{event.location}</p>
                    <p className="m-0 text-600">What3words: {event.what3words}</p>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="mb-3">
                {event.status === 'full' ? (
                  <Tag severity="danger" value="FULL - Waitlist Available" icon="pi pi-exclamation-triangle" />
                ) : event.status === 'available' ? (
                  <Tag severity="success" value="Available to Book" icon="pi pi-check" />
                ) : (
                  <Tag severity="warning" value="Cancelled" icon="pi pi-times" />
                )}
              </div>
            </div>

            {/* Map */}
            <div className="flex-none">
              <Card className="p-0">
                <img 
                  src={event.mapUrl} 
                  alt="Event Location Map" 
                  className="w-full"
                  style={{ maxWidth: '300px' }}
                />
                <div className="p-2 text-center">
                  <a 
                    href={`https://maps.google.co.uk/maps?q=${encodeURIComponent(event.location)}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary text-sm"
                  >
                    View on Google Maps
                  </a>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Classes and Pricing */}
      <div className="se-card mb-4">
        <div className="se-card-header">
          <h2>Classes & Pricing</h2>
        </div>
        <div className="se-card-content">
          <div className="grid">
            {event.classes.map((cls) => (
              <div key={cls.id} className="col-12 md:col-4">
                <Card className="h-full text-center">
                  <h3 className="mt-0 mb-2">{cls.name}</h3>
                  <div className="mb-3">
                    <div className="text-2xl font-bold se-text-primary">
                      £{cls.memberPrice - cls.earlyBirdDiscount}
                    </div>
                    <div className="text-sm text-600">
                      Members (with early bird discount)
                    </div>
                    {cls.earlyBirdDiscount > 0 && (
                      <div className="text-xs text-600">
                        (Regular: £{cls.memberPrice}, Non-member: £{cls.nonMemberPrice})
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            ))}
          </div>
          
          <Message 
            severity="info" 
            text={`Early bird discount available until ${event.lastCancellationDate}. No credits or refunds after this date.`}
            className="mt-3"
          />
        </div>
      </div>

      {/* Booking Section */}
      {event.status !== 'cancelled' && (
        <div className="se-card mb-4">
          <div className="se-card-header">
            <h2>{event.status === 'full' ? 'Join Waitlist' : 'Book This Event'}</h2>
          </div>
          <div className="se-card-content">
            {event.status === 'full' ? (
              <div>
                <Message 
                  severity="warning" 
                  text="This event has reached capacity. Join the waitlist to be notified when places become available."
                  className="mb-3"
                />
                <div className="flex justify-content-end align-items-center">
                  <Button 
                    label="Join Waitlist" 
                    icon="pi pi-plus" 
                    className="p-button-warning"
                    onClick={handleWaitlist}
                  />
                </div>
              </div>
            ) : (
              <div className="grid">
                <div className="col-12 md:col-4">
                  <div className="se-form-group">
                    <label className="se-form-label">Select Horse *</label>
                    <Dropdown
                      value={selectedHorse}
                      options={horses}
                      onChange={(e) => setSelectedHorse(e.value)}
                      placeholder="Choose your horse"
                      className="w-full"
                    />
                  </div>
                </div>
                
                <div className="col-12 md:col-4">
                  <div className="se-form-group">
                    <label className="se-form-label">Select Class *</label>
                    <Dropdown
                      value={selectedClassification}
                      options={event.classes.map(c => ({ label: c.name, value: c.id }))}
                      onChange={(e) => setSelectedClassification(e.value)}
                      placeholder="Choose your class"
                      className="w-full"
                    />
                  </div>
                </div>
                
                <div className="col-12 md:col-4">
                  <div className="se-form-group">
                    <label className="se-form-label">Comments</label>
                    <InputTextarea
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      placeholder="Travel arrangements, special requirements, etc."
                      rows={3}
                      className="w-full"
                    />
                  </div>
                </div>
                
                <div className="col-12">
                  <Button 
                    label="Book Event" 
                    icon="pi pi-shopping-cart" 
                    className="p-button-primary"
                    onClick={handleBooking}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Event Details */}
      <div className="grid">
        <div className="col-12 md:col-6">
          <div className="se-card h-full">
            <div className="se-card-header">
              <h3>Event Details</h3>
            </div>
            <div className="se-card-content">
              <div className="mb-3">
                <strong>Venue Type:</strong>
                <p className="mt-1 mb-0">{event.venueType}</p>
              </div>
              
              <div className="mb-3">
                <strong>Facilities:</strong>
                <p className="mt-1 mb-0">{event.facilities}</p>
              </div>
              
              <div className="mb-3">
                <strong>On-day Entry:</strong>
                <p className="mt-1 mb-0">{event.onDayEntry}</p>
              </div>
              
              {event.photographer && (
                <div className="mb-3">
                  <strong>Photographer:</strong>
                  <p className="mt-1 mb-0">
                    <a href={`http://${event.photographer}`} target="_blank" rel="noopener noreferrer">
                      {event.photographer}
                    </a>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="col-12 md:col-6">
          <div className="se-card h-full">
            <div className="se-card-header">
              <h3>Contact & Information</h3>
            </div>
            <div className="se-card-content">
              <div className="mb-3">
                <strong>Ride Organizer:</strong>
                <p className="mt-1 mb-0">{event.organizer.name}</p>
                <p className="mt-1 mb-0">
                  <a href={`mailto:${event.organizer.email}`}>{event.organizer.email}</a>
                </p>
              </div>
              
              <div className="mb-3">
                <strong>Cancellation Deadline:</strong>
                <p className="mt-1 mb-0">{event.lastCancellationDate}</p>
              </div>
              
              <div className="flex flex-column gap-2 mt-3">
                <Button 
                  label="Rules & Etiquette" 
                  icon="pi pi-book" 
                  className="p-button-outlined p-button-sm"
                  onClick={() => window.open('/rules-etiquette', '_blank')}
                />
                <Button 
                  label="Cancellation Policy" 
                  icon="pi pi-info-circle" 
                  className="p-button-outlined p-button-sm"
                  onClick={() => window.open('/cancellation-policy', '_blank')}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Checkout Dialog */}
      <CheckoutDialog
        visible={checkoutVisible}
        onHide={() => setCheckoutVisible(false)}
        item={{
          ...event,
          selectedClassification,
          selectedHorse,
          comments
        }}
        itemType="event"
      />
    </div>
  );
};

export default EventDetailBooking;